import { useMemo, useState } from "react";
import {
  AlertIcon,
  CheckIcon,
  ChildrenIcon,
  DataSurface,
  InfoIcon,
  Note,
  StatusPill,
  TableCard,
  Td,
  Thead,
  Tr,
} from "../../../shared/components";
import { useResource } from "../../../shared/hooks/useResource";
import {
  createCoupon,
  formatPaise,
  getCoupons,
  getPlans,
  getRedemptions,
  getSubscriptions,
  updateCoupon,
  type CouponRow,
  type PlanRow,
  type RedemptionRow,
  type SubscriptionRow,
} from "../../../shared/api/billing";
import type { StaffAuth } from "../../../shared/api/teaching";
import type { Lang } from "../../../shared/i18n";
import { BILLING_COPY } from "./billingCopy";
import { quote, type Quote } from "./discountModel";
import {
  couponDraftIsValid,
  describeCouponDraft,
  expiryFromDays,
  pickableSchools,
  schoolStateLabel,
  validateCouponDraft,
  COUPON_CODE_MAX,
  COUPON_NAME_MAX,
  type CouponDraft,
  type CouponKindChoice,
} from "./couponModel";

export interface PlatformCouponsScreenProps {
  lang?: Lang;
  auth?: StaffAuth;
}

export function PlatformCouponsScreen({
  lang = "en",
  auth,
}: PlatformCouponsScreenProps) {
  const t = BILLING_COPY[lang];
  const plans = useResource<PlanRow[]>(
    (signal) => getPlans({ ...auth, signal }),
    ["plans"],
  );

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* Top Header */}
      <div>
        <h1 className="font-dash text-dash-xl font-extrabold text-ink">{t.couponsTitle}</h1>
        <p className="mt-1 max-w-[60ch] font-dash text-dash-sm text-muted">
          {t.couponsSubtitle}
        </p>
      </div>

      {/* Card 1: Discount Plan (Calculator) */}
      <DiscountCalculator lang={lang} plans={plans.data ?? []} />

      {/* Card 2, 3, 4 & 5: Catalogue, Creation, Redemptions & Help */}
      <CouponCatalogue lang={lang} auth={auth} />
    </section>
  );
}

/**
 * List price → discount → what they pay, for a plan and a school size.
 */
function DiscountCalculator({
  lang,
  plans,
}: {
  lang: Lang;
  plans: readonly PlanRow[];
}) {
  const t = BILLING_COPY[lang];
  const [planId, setPlanId] = useState("");
  const [seats, setSeats] = useState("50");
  const [kind, setKind] = useState<CouponKindChoice>("PER_STUDENT");
  const [percent, setPercent] = useState("12");
  const [amount, setAmount] = useState("");

  const plan = useMemo(
    () => plans.find((p) => p.id === planId) ?? null,
    [plans, planId],
  );

  const result: Quote | null = useMemo(
    () =>
      plan === null
        ? null
        : quote({
            perStudentPaise: plan.perStudentPaise,
            floorPaise: plan.floorPaise,
            seats: Number.parseInt(seats, 10),
            percentOff: kind === "PERCENT" ? Number.parseInt(percent, 10) : null,
            amountOff: kind === "AMOUNT" ? amount : null,
            perStudentOff: kind === "PER_STUDENT" ? amount : null,
          }),
    [plan, seats, kind, percent, amount],
  );

  const currency = plan?.currency ?? "INR";

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <h2 className="text-dash-lg font-extrabold text-ink">{t.calcTitle}</h2>
      <p className="text-dash-xs text-muted max-w-2xl mb-4">{t.calcHint}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="calc-plan">
            {t.calcPlan}
          </label>
          <select
            id="calc-plan"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
          >
            <option value="">{t.calcNoPlan}</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="calc-seats">
            {t.calcSeats}
          </label>
          <input
            id="calc-seats"
            inputMode="numeric"
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
            className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
          />
        </div>

        <div>
          <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="calc-kind">
            {t.couponKind}
          </label>
          <select
            id="calc-kind"
            value={kind}
            onChange={(e) => { setKind(e.target.value as CouponKindChoice) }}
            className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
          >
            <option value="PER_STUDENT">{t.kindPerStudent}</option>
            <option value="PERCENT">{t.kindPercent}</option>
            <option value="AMOUNT">{t.kindAmount}</option>
          </select>
        </div>

        <div>
          <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="calc-pct">
            {kind === "PERCENT"
              ? t.percentOff
              : kind === "PER_STUDENT"
                ? t.perStudentOff
                : t.amountOff}
          </label>
          {kind === "PERCENT" ? (
            <input
              id="calc-pct"
              inputMode="numeric"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
            />
          ) : (
            <input
              id="calc-amt"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
            />
          )}
        </div>
      </div>

      {result === null ? (
        <div className="rounded-xl border border-amber-200/80 bg-amber-50/90 p-3.5 px-4 flex items-center gap-2.5 text-dash-xs font-medium text-amber-900 shadow-xs">
          <InfoIcon size={16} className="text-amber-600 shrink-0" />
          <span>{t.calcNoPlan}</span>
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-canvas p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <Figure
              label={t.calcList}
              value={formatPaise(result.grossPaise, currency)}
              struck={result.discountPaise > 0}
            />
            <Figure
              label={t.calcOff}
              value={`− ${formatPaise(result.discountPaise, currency)}`}
              coral
            />
            <Figure
              label={t.calcNet}
              value={formatPaise(result.netPaise, currency)}
              big
            />
          </div>

          <p className="text-dash-xs text-muted">
            {t.calcPerStudent(formatPaise(result.perStudentPaise, currency))}
          </p>

          {result.floorApplied && (
            <div className="max-w-prose">
              <Note tone="info" icon={<InfoIcon size={16} />}>
                {t.calcFloorApplied(formatPaise(result.floorPaise, currency))}
              </Note>
            </div>
          )}

          {result.discountPaise === 0 && (
            <div className="max-w-prose">
              <Note tone="warning" icon={<AlertIcon size={16} />}>
                {t.calcNothingOff}
              </Note>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** One figure in the three-across row. Tabular, because they are compared. */
function Figure({
  label,
  value,
  struck = false,
  big = false,
  coral = false,
}: {
  label: string;
  value: string;
  struck?: boolean;
  big?: boolean;
  coral?: boolean;
}) {
  return (
    <span className="block">
      <span className="block text-dash-xs font-bold text-muted">{label}</span>
      <span
        className={`tabular mt-1 block font-extrabold ${big ? "text-dash-xl" : "text-dash-lg"} ${
          struck ? "text-muted line-through" : "text-ink"
        }`}
        style={coral && !struck ? { color: "var(--color-coral)" } : undefined}
      >
        {value}
      </span>
    </span>
  );
}

/**
 * Every discount an operator has defined, and every school that claimed one.
 */
function CouponCatalogue({ lang, auth }: { lang: Lang; auth?: StaffAuth }) {
  const t = BILLING_COPY[lang];
  const coupons = useResource<CouponRow[]>(
    (signal) => getCoupons({ ...auth, signal }),
    ["coupons"],
    {
      isEmpty: (rows) => rows.length === 0,
    },
  );

  /*
   * 🔴 THE SCHOOLS THE COUPON MAY BE BOUND TO, FETCHED RATHER THAN TYPED.
   * `/superadmin/subscriptions` returns every school with its plan and its trial
   * state — which is exactly what change 19 §16 asks the picker to distinguish,
   * and it is one request rather than a second endpoint.
   */
  const schools = useResource<SubscriptionRow[]>(
    (signal) => getSubscriptions({ ...auth, signal }),
    ["coupon-schools"],
  );
  const pickable = useMemo(() => pickableSchools(schools.data ?? []), [schools.data]);

  const [draft, setDraft] = useState<CouponDraft>({
    code: "",
    name: "",
    kind: "PER_STUDENT",
    percent: "",
    amount: "",
    expiresInDays: "",
    durationPeriods: "",
    maxRedemptions: "",
    schoolId: "",
    newSchoolsOnly: false,
  });
  const [startsAt, setStartsAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const errors = validateCouponDraft(draft);
  const complete = couponDraftIsValid(draft);
  const set = <K extends keyof CouponDraft>(field: K, value: CouponDraft[K]) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setFailed(null);
    setCreated(null);
  };
  const show = (field: keyof typeof errors) => (submitted ? errors[field] : null);
  /* Recomputed on every render on purpose: an operator who leaves the form open
     over midnight must not create a coupon dated from yesterday. */
  const expiresAt = expiryFromDays(draft.expiresInDays, new Date());

  const [searchQuery, setSearchQuery] = useState("");

  const submit = async () => {
    setSubmitted(true);
    /* The guard `PlanCatalogue` was missing: a body that cannot succeed is not
       sent, so a 400 never has to be explained as something it is not. */
    if (!couponDraftIsValid(draft)) return;

    setBusy(true);
    setFailed(null);
    setCreated(null);
    try {
      const row = await createCoupon(
        {
          code: draft.code.trim().toUpperCase(),
          name: draft.name.trim(),
          /* 🔴 EXACTLY ONE OF THE THREE, WHICH IS WHAT THE SERVER REFUSES ON.
             The two flat kinds share a column; `kind` is derived from which
             field arrives, so sending both would be a coupon worth either ₹10
             or ₹10 × the whole school. */
          ...(draft.kind === "PERCENT"
            ? { percentOff: Number.parseInt(draft.percent.trim(), 10) }
            : draft.kind === "PER_STUDENT"
              ? { perStudentOffPaise: draft.amount.trim() }
              : { amountOffPaise: draft.amount.trim() }),
          durationPeriods:
            draft.durationPeriods.trim() === ""
              ? null
              : Number.parseInt(draft.durationPeriods.trim(), 10),
          maxRedemptions:
            draft.maxRedemptions.trim() === ""
              ? null
              : Number.parseInt(draft.maxRedemptions.trim(), 10),
          /* 🔴 AN ID FROM THE PICKER OR NOTHING. This used to be whatever was
             typed into a text box, which produced a coupon bound to a school
             that does not exist and refusing everybody forever. */
          schoolId: draft.schoolId === "" ? null : draft.schoolId,
          newSchoolsOnly: draft.newSchoolsOnly,
          startsAt: startsAt === "" ? null : `${startsAt}T00:00:00.000Z`,
          expiresAt,
        },
        auth,
      );
      setCreated(row.code);
      setDraft({
        code: "",
        name: "",
        kind: "PER_STUDENT",
        percent: "",
        amount: "",
        expiresInDays: "",
        durationPeriods: "",
        maxRedemptions: "",
        schoolId: "",
        newSchoolsOnly: false,
      });
      setStartsAt("");
      setSubmitted(false);
      coupons.retry();
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      setFailed(
        status === 400 || status === 404
          ? t.couponRejectedOnCreate
          : t.writeFailed,
      );
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (coupon: CouponRow) => {
    await updateCoupon(coupon.id, { isActive: !coupon.isActive }, auth).catch(
      () => undefined,
    );
    coupons.retry();
  };

  const filteredCoupons = useMemo(() => {
    const list = coupons.data ?? [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q),
    );
  }, [coupons.data, searchQuery]);

  return (
    <div className="space-y-6">
      {/* Card 2: Discount Details (Create Form) */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
        <h2 className="text-dash-lg font-extrabold text-ink">{t.coupons}</h2>
        <p className="text-dash-xs text-muted max-w-2xl mb-4">{t.couponsHint}</p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {/* Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-code">
                {t.couponCode} <span className="text-rose-500">*</span>
              </label>
              <input
                id="cp-code"
                placeholder="e.g. PILOT-2026"
                value={draft.code}
                maxLength={COUPON_CODE_MAX}
                aria-invalid={show("code") !== null}
                onChange={(e) => { set("code", e.target.value.toUpperCase()) }}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
              />
              <p className="mt-1 text-dash-xs text-muted">{show("code") ?? t.couponCodeHint}</p>
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-name">
                {t.couponName} <span className="text-rose-500">*</span>
              </label>
              <input
                id="cp-name"
                placeholder="e.g. Kerala pilot, first term"
                value={draft.name}
                maxLength={COUPON_NAME_MAX}
                aria-invalid={show("name") !== null}
                onChange={(e) => { set("name", e.target.value) }}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
              />
              <p className="mt-1 text-dash-xs text-muted">{show("name") ?? t.couponNameHint}</p>
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-kind">
                {t.couponKind} <span className="text-rose-500">*</span>
              </label>
              <select
                id="cp-kind"
                value={draft.kind}
                onChange={(e) => { set("kind", e.target.value as CouponKindChoice) }}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
              >
                {/* 🔴 PER-CHILD IS FIRST BECAUSE IT IS THE ORDINARY CASE. Every
                    plan in this product is priced per child, so a concession is
                    negotiated the same way; the flat kinds are the exceptions. */}
                <option value="PER_STUDENT">{t.kindPerStudent}</option>
                <option value="PERCENT">{t.kindPercent}</option>
                <option value="AMOUNT">{t.kindAmount}</option>
              </select>
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-pct">
                {draft.kind === "PERCENT"
                  ? `${t.percentOff} *`
                  : draft.kind === "PER_STUDENT"
                    ? `${t.perStudentOff} *`
                    : `${t.amountOff} *`}
              </label>
              {draft.kind === "PERCENT" ? (
                <input
                  id="cp-pct"
                  placeholder="e.g. 12"
                  inputMode="numeric"
                  value={draft.percent}
                  aria-invalid={show("value") !== null}
                  onChange={(e) => { set("percent", e.target.value) }}
                  className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
                />
              ) : (
                <input
                  id="cp-pct"
                  placeholder={draft.kind === "PER_STUDENT" ? "e.g. 10" : "e.g. 5000"}
                  inputMode="decimal"
                  value={draft.amount}
                  aria-invalid={show("value") !== null}
                  onChange={(e) => { set("amount", e.target.value) }}
                  className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
                />
              )}
              <p className="mt-1 text-dash-xs text-muted">
                {show("value") ??
                  (draft.kind === "PERCENT"
                    ? t.percentHint
                    : draft.kind === "PER_STUDENT"
                      ? t.perStudentOffHint
                      : t.amountOffHint)}
              </p>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-dur">
                {t.duration} <span className="text-rose-500">*</span>
              </label>
              <input
                id="cp-dur"
                placeholder="every period"
                inputMode="numeric"
                value={draft.durationPeriods}
                aria-invalid={show("durationPeriods") !== null}
                onChange={(e) => { set("durationPeriods", e.target.value) }}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
              />
              <p className="mt-1 text-dash-xs text-muted">{show("durationPeriods") ?? t.durationHint}</p>
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-max">
                {t.maxRedemptions} <span className="text-rose-500">*</span>
              </label>
              <input
                id="cp-max"
                placeholder="no limit"
                inputMode="numeric"
                value={draft.maxRedemptions}
                aria-invalid={show("maxRedemptions") !== null}
                onChange={(e) => { set("maxRedemptions", e.target.value) }}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
              />
              <p className="mt-1 text-dash-xs text-muted">{show("maxRedemptions") ?? t.maxRedemptionsHint}</p>
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-school">
                {t.boundSchool} (optional)
              </label>
              {/* 🔴 A LIST, NOT A TEXT BOX. This field used to be a free input
                  labelled "Search school name" whose value was sent as
                  `schoolId`: it searched nothing, and anything typed into it
                  bound the coupon to a school that does not exist — a code that
                  then refuses every school forever, reading as "not for you". */}
              <select
                id="cp-school"
                value={draft.schoolId}
                disabled={schools.state === "loading"}
                onChange={(e) => { set("schoolId", e.target.value) }}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
              >
                <option value="">{t.anySchool}</option>
                {pickable.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name} — {schoolStateLabel(school)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-dash-xs text-muted">
                {schools.state === "error" ? t.schoolsLoadFailed : t.boundSchoolHint}
              </p>
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start mb-6">
            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-start">
                {t.couponStartsAt} <span className="text-rose-500">*</span>
              </label>
              <input
                id="cp-start"
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
              />
              <p className="mt-1 text-dash-xs text-muted">{t.couponStartsAtHint}</p>
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="cp-exp">
                {t.expiresInDays}
              </label>
              {/* 🔴 DAYS, NOT A DATE. Nobody agrees a discount "until 12
                  November" — they agree it for a term or a fortnight, and a date
                  picker made the operator do calendar arithmetic on a phone call.
                  The date it works out to is printed below so nothing is
                  implicit. */}
              <input
                id="cp-exp"
                placeholder="never"
                inputMode="numeric"
                value={draft.expiresInDays}
                aria-invalid={show("expiresInDays") !== null}
                onChange={(e) => { set("expiresInDays", e.target.value) }}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
              />
              <p className="mt-1 text-dash-xs text-muted">
                {show("expiresInDays") ??
                  (expiresAt === null
                    ? t.expiresNever
                    : t.expiresOn(
                        new Date(expiresAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          timeZone: "UTC",
                        }),
                      ))}
              </p>
            </div>

            <div className="pt-2 sm:pt-6">
              <label className="flex items-center gap-2 cursor-pointer text-dash-xs font-bold text-ink hover:text-pink-600 transition-colors">
                <input
                  type="checkbox"
                  checked={draft.newSchoolsOnly}
                  onChange={(e) => { set("newSchoolsOnly", e.target.checked) }}
                  className="h-4 w-4 rounded border-line text-[#E6007A] focus:ring-[#E6007A] accent-[#E6007A]"
                />
                <span>{t.newSchoolsOnly}</span>
              </label>
              <p className="mt-1 text-dash-xs text-muted">{t.newSchoolsOnlyHint}</p>
            </div>
          </div>

          {/* 🔴 THE LAST PLACE THE PER-CHILD/FLAT DIFFERENCE IS VISIBLE BEFORE
              THE WRITE. Everything above is a field; this is the coupon, in a
              sentence, including the date "30 days" actually works out to. */}
          {complete ? (
            <p className="mb-3 font-dash text-dash-xs text-muted">
              {describeCouponDraft(draft, expiresAt)}
            </p>
          ) : null}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={busy || !complete}
              className="bg-[#E6007A] text-white px-6 py-2.5 rounded-full font-bold text-dash-xs hover:bg-[#C40068] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? t.creating : t.createCoupon}
            </button>
          </div>

          {failed !== null && (
            <div className="mt-4">
              <Note tone="warning" icon={<AlertIcon size={16} />}>
                {failed}
              </Note>
            </div>
          )}

          {created !== null && (
            <div className="mt-4">
              <Note tone="success" icon={<CheckIcon size={16} />}>
                {t.couponCreated(created)}
              </Note>
            </div>
          )}
        </form>
      </div>

      {/* Card 3: Discounts Created (Table) */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-dash-lg font-extrabold text-ink">Discounts created</h2>
            <p className="text-dash-xs text-muted mt-0.5">All discount codes you&apos;ve created.</p>
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="Search by code or name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-72 rounded-full border border-line bg-white px-4 py-2 text-dash-xs pr-9 focus:border-primary focus:outline-none transition-colors"
            />
            <svg
              className="absolute right-3 top-2.5 h-4 w-4 text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <DataSurface
          state={coupons.state}
          skeletonRows={3}
          emptyMessage={t.noCoupons}
          errorMessage={t.loadFailed}
          errorRetryLabel={t.retry}
          onRetry={coupons.retry}
        >
          <TableCard>
            <table className="w-full border-collapse">
              <Thead
                headers={[
                  t.couponCode,
                  t.couponName,
                  t.discount,
                  t.duration,
                  t.claims,
                  t.couponWindow,
                  t.visibility,
                  "Actions",
                ]}
              />
              <tbody>
                {filteredCoupons.map((c) => (
                  <Tr key={c.id}>
                    <Td bold>{c.code}</Td>
                    <Td soft>{c.name}</Td>
                    {/* 🔴 "/child" IS PART OF THE FIGURE, NOT A DETAIL. `off`
                        renders identically for both flat kinds — they share the
                        column — so a per-child coupon shown as a bare "₹10" in
                        this table is a discount understated by the whole
                        enrolment of every school that claims it. */}
                    <Td num>
                      {c.kind === "PERCENT"
                        ? `${c.off}`
                        : `${formatPaise(c.amountOffPaise ?? 0, c.currency)}${
                            c.kind === "PER_STUDENT" ? t.perChildSuffix : ""
                          }`}
                    </Td>
                    <Td soft>
                      {c.durationPeriods === null
                        ? t.couponForever
                        : t.couponPeriodsLeft(c.durationPeriods)}
                    </Td>
                    <Td num>{t.claimsOf(c.redeemedCount, c.maxRedemptions)}</Td>
                    <Td soft>{couponWindow(c, t)}</Td>
                    <Td>
                      <span className="flex flex-wrap gap-1.5">
                        <StatusPill
                          tone={c.isActive ? "success" : "neutral"}
                          icon={
                            c.isActive ? (
                              <CheckIcon size={14} />
                            ) : (
                              <InfoIcon size={14} />
                            )
                          }
                        >
                          {c.isActive ? t.live : t.retired}
                        </StatusPill>
                        {c.startsAt !== null &&
                          new Date(c.startsAt).getTime() > Date.now() && (
                            <StatusPill tone="warning" icon={<InfoIcon size={14} />}>
                              {t.couponNotYetLive}
                            </StatusPill>
                          )}
                        {c.newSchoolsOnly && (
                          <StatusPill
                            tone="neutral"
                            icon={<ChildrenIcon size={14} />}
                          >
                            {t.newSchoolsOnly}
                          </StatusPill>
                        )}
                      </span>
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => void toggle(c)}
                        className="text-dash-xs font-bold text-pink-600 hover:text-pink-700 hover:underline transition-colors"
                      >
                        {c.isActive ? t.retireCoupon : t.restoreCoupon}
                      </button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        </DataSurface>
      </div>

      {/* Card 4: Recently Claimed (Redemptions) */}
      <RedemptionsPanel lang={lang} auth={auth} />

      {/* Card 5: Need Help? Banner */}
      <div className="rounded-2xl border border-pink-100 bg-pink-50/50 p-5 px-6 flex items-center gap-4 shadow-xs">
        <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        </div>
        <div>
          <h3 className="text-dash-sm font-extrabold text-ink">Need help?</h3>
          <p className="text-dash-xs text-muted mt-0.5">
            Discounts apply at enrollment. Schools see the discount on the payment page when they qualify.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * What each school was given, and what it came to on the day.
 */
function RedemptionsPanel({ lang, auth }: { lang: Lang; auth?: StaffAuth }) {
  const t = BILLING_COPY[lang];
  const rows = useResource<RedemptionRow[]>(
    (signal) => getRedemptions({ ...auth, signal }),
    ["redemptions"],
    { isEmpty: (r) => r.length === 0 },
  );

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
      <div>
        <h2 className="text-dash-lg font-extrabold text-ink">{t.redemptions}</h2>
        <p className="text-dash-xs text-muted mt-0.5">{t.redemptionsHint}</p>
      </div>

      <DataSurface
        state={rows.state}
        skeletonRows={3}
        emptyMessage={t.noRedemptions}
        errorMessage={t.loadFailed}
        errorRetryLabel={t.retry}
        onRetry={rows.retry}
      >
        <TableCard>
          <table className="w-full border-collapse">
            <Thead
              headers={[
                t.school,
                t.couponCode,
                t.when,
                t.saved,
                t.calcPlan,
              ]}
            />
            <tbody>
              {(rows.data ?? []).map((r) => (
                <Tr key={r.id}>
                  <Td bold>{r.schoolName}</Td>
                  <Td soft>{r.couponCode}</Td>
                  <Td soft>{new Date(r.createdAt).toLocaleString()}</Td>
                  <Td num>{formatPaise(r.discountPaise, r.currency)}</Td>
                  <Td soft>{r.seats} children</Td>
                </Tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      </DataSurface>
    </div>
  );
}

/**
 * Both ends of a coupon's window, as one cell.
 */
function couponWindow(
  coupon: CouponRow,
  t: (typeof BILLING_COPY)["en"],
): string {
  const from =
    coupon.startsAt === null ? null : new Date(coupon.startsAt).toLocaleDateString();
  const until =
    coupon.expiresAt === null ? null : new Date(coupon.expiresAt).toLocaleDateString();
  if (from === null && until === null) return t.couponWindowOpen;
  if (from === null) return `→ ${String(until)}`;
  if (until === null) return `${from} →`;
  return `${from} → ${until}`;
}
